import { BusinessOutlined, CalendarTodayOutlined, DescriptionOutlined, FileDownloadOutlined, ViewListRounded, ViewModuleRounded, Visibility } from "@mui/icons-material";
import { Alert, Box, Button, Card, Chip, Divider, Grid, IconButton, LinearProgress, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { deleteBulkKyb, exportKybReport, getKybOrgSummary } from "api/services/clients/client-kyb";
import FloatingButton from "components/FloatingButton";
import SearchContainer from "components/SearchContainer";
import Table from "components/Table";
import { snack } from "components/toast";
import { useConfirm } from "context/ConfirmDialog";
import { documentTypes } from "data/constants";
import useTitle from "hooks/useTitle";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { ResType } from "types";
import { handleError } from "utils/handleError";
import AddKYBOrg from "views/register/AddKYBOrg";
import { useDispatch } from "react-redux";
import { resetFilters } from "redux/reducers/taskboardSlice";
import ValidateAccess from "components/ValidateAccess";
import { Permissions } from "data/permissons";
import KybExpiryView from "views/register/KybExpiryView";
import "../GridStyling.css";
import { resetState } from "redux/reducers/clientsSlice";
import { getCurrentFormattedDateTime } from "utils";
import sortHandler from "components/Table/SortHandler";
import _ from "lodash";
import { SortDirection } from "data/constants";
import excelIcon from "./../../assets/images/128px-Microsoft_Office_Excel_(2019–2025).svg.png";



type Props = {
  data: any;
};

function ExpiryMgmt(state) {
  const navigate = useNavigate();

  const { mutate, isLoading: isDeleting } = useMutation(deleteBulkKyb, {
    onSuccess: () => {
      snack.success("License Management Records Deleted");
      queryClient.invalidateQueries("kyb-org-summary");
      setOpen(false);
      setSelected([]);
    },
    onError: (err: any) => {
      snack.error(err.response.data.message);
    },
  });

  const { mutate: mutate1, isLoading: isExporting } = useMutation(exportKybReport, {
    onSuccess: (res: any) => {
      const arr = new Uint8Array(res.data?.data);
      const blob = new Blob([arr], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const file = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      const formattedDateTime = getCurrentFormattedDateTime();
      link.href = file;
      link.download = `ATOM - License Management Report ${formattedDateTime}.xlsx`;
      link.click();
      snack.success("License Management records exported successfully");
    },
    onError: (err: any) => {
      snack.error(handleError(err));
    },
  });

  useTitle("License Management");
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState<number>(10);
  const [offset, setOffset] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<any[]>([]);
  const dispatch = useDispatch();
  const [viewRecord, setViewRecord] = useState<any>(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [columns, setColumns] = useState(_.cloneDeep(defaultColumns));
  const [sort, setSort] = useState<any>({});
  const [viewMode, setViewMode] = useState<"list" | "card">("card");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  useEffect(() => {
    return () => {
      dispatch(resetFilters());
      dispatch(resetState());
    };
  }, []);
  const { isLoading, data: orgSummaryData, error }: ResType = useQuery(
    ["kyb-org-summary", { search, limit, offset: offset * limit }],
    getKybOrgSummary,
    { retry: false }
  );
  const records: any[] = orgSummaryData?.data?.records || [];

  const summary = useMemo(() => {
    const startOfWeek = moment().startOf("week");
    const endOfWeek = moment().endOf("week");
    const startOfMonth = moment().startOf("month");
    const endOfMonth = moment().endOf("month");
    let totalDocuments = 0, expiringThisWeek = 0, expiringThisMonth = 0, validDocuments = 0, expiredDocuments = 0;
    records.forEach((row: any) => {
      totalDocuments++;
      if (!row.isExpiry || !row.expiryDate) { validDocuments++; return; }
      const expiry = moment(row.expiryDate).startOf("day");
      const days = expiry.diff(moment().startOf("day"), "days");
      if (days < 0) { expiredDocuments++; }
      else if (expiry.isBetween(startOfWeek, endOfWeek, undefined, "[]")) { expiringThisWeek++; }
      else if (expiry.isBetween(startOfMonth, endOfMonth, undefined, "[]")) { expiringThisMonth++; }
      else { validDocuments++; }
    });
    return { totalDocuments, expiringThisWeek, expiringThisMonth, validDocuments, expiredDocuments };
  }, [records]);

  const filteredRecords = useMemo(() => {
    if (activeFilter === "all") return records;
    const startOfWeek = moment().startOf("week");
    const endOfWeek = moment().endOf("week");
    const startOfMonth = moment().startOf("month");
    const endOfMonth = moment().endOf("month");
    return records.filter((row: any) => {
      const hasExpiry = row.isExpiry && row.expiryDate;
      const expiry = hasExpiry ? moment(row.expiryDate).startOf("day") : null;
      const days = expiry ? expiry.diff(moment().startOf("day"), "days") : null;
      if (activeFilter === "expired") return hasExpiry && days! < 0;
      if (activeFilter === "this_week") return hasExpiry && days! >= 0 && expiry!.isBetween(startOfWeek, endOfWeek, undefined, "[]");
      if (activeFilter === "this_month") return hasExpiry && days! >= 0 && expiry!.isBetween(startOfMonth, endOfMonth, undefined, "[]");
      if (activeFilter === "valid") return !hasExpiry || (days! >= 0 && !expiry!.isBetween(startOfMonth, endOfMonth, undefined, "[]"));
      return true;
    });
  }, [records, activeFilter]);

  const handleDelete = () => {
    confirm({
      msg: "Are you sure you want to delete the selected KYB records?",
      action: () => {
        mutate({
          body: { kyb: selected },
        });
      },
    });
  };

  if (error) {
    return (
      <Alert sx={{ maxWidth: 500, margin: "auto", mt: 5 }} severity="error">
        {handleError(error)}
      </Alert>
    );
  }

  const handleExport = () => {
    if (isExporting) return;
    mutate1({
      ...state,
      type: "ORGANIZATION",
      search,
    });
  };

  const setSearchChange = (e) => {
    setSearch(e);
    setOffset(0);
  };
  const handleSort = (v: any) => {
    sortHandler({
      key: v.key,
      columns,
      sortState: sort,
      setSortState: setSort,
      setColumns: setColumns,
    });
  };


  const summaryCards = [
    { label: "TOTAL DOCUMENTS", value: summary?.totalDocuments ?? 0, color: "#5696d5", sub: "Total KYB documents tracked", filterKey: "all" },
    { label: "EXPIRING THIS WEEK", value: summary?.expiringThisWeek ?? 0, color: "#d85d5d", sub: "Immediate action needed", filterKey: "this_week" },
    { label: "EXPIRING THIS MONTH", value: (summary?.expiringThisMonth ?? 0) + (summary?.expiringThisWeek ?? 0), color: "#f1ab65", sub: "Requires attention this month", filterKey: "this_month" },
    { label: "VALID & TRACKED", value: summary?.validDocuments ?? 0, color: "#14B8A6", sub: "All reminders configured", filterKey: "valid" },
    { label: "EXPIRED DOCUMENTS", value: summary?.expiredDocuments ?? 0, color: "#9CA3AF", sub: "Total KYB documents expired", filterKey: "expired" },
  ];

  return (
    <Box pt={3} pl={1} pr={1} pb={0}>
      {/* Summary Cards */}
      <Grid container spacing={2} mb={3} pl={2} pr={2}>
        {summaryCards.map((card, i) => {
          const isActive = activeFilter === card.filterKey;
          return (
            <Grid item xs={12} sm={6} md={2.4} key={i}>
              <Box
                onClick={() => setActiveFilter(card.filterKey)}
                sx={{
                  borderTop: `4px solid ${card.color}`,
                  borderRadius: 2,
                  p: 2.5,
                  boxShadow: isActive ? `0 0 0 2px ${card.color}` : `0 1px 2px ${card.color}`,
                  bgcolor: isActive ? `${card.color}10` : "white",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  "&:hover": { boxShadow: `0 0 0 2px ${card.color}`, bgcolor: `${card.color}10` },
                }}
              >
                <Typography variant="h3" fontWeight={700} color={card.color} lineHeight={1.1}>
                  {card.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5} display="block" mt={0.5}>
                  {card.label}
                </Typography>
                <Typography variant="caption" color={card.color} display="block" mt={1}>
                  {card.sub}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={2} justifyContent="space-between" alignItems="center" mb={2} pl={2} pr={2}>
        <Grid item xs={12} md={4} display="flex" justifyContent={{ xs: 'center', md: 'flex-start' }} sx={{ mt: { xs: 3, md: 0 } }}>
          <SearchContainer
            value={search}
            debounced
            minWidth={isSmallScreen ? "280px" : "500px"}
            onChange={setSearchChange}
            placeHolder="Search by Client | Document ID | Document Type"
          />
        </Grid>
        <Grid item xs={12} md={4} display="flex" justifyContent={{ xs: 'center', md: 'flex-end' }} sx={{ mt: { xs: 2, md: 0 } }}>
          <Box className="text-right" gap={2}>

            {selected.length > 0 && <ValidateAccess name={Permissions.DELETE_EXPIRY_MANAGEMENT}>
              <Button variant="outlined" color="secondary" sx={{ mb: 1.5 }} onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </ValidateAccess>
            }

            <Box display="inline-flex" alignItems="center" sx={{ border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden", mx: 2 }}>
              <Tooltip title="Card view">
                <IconButton
                  size="small"
                  onClick={() => setViewMode("card")}
                  sx={{ borderRadius: 0, bgcolor: viewMode === "card" ? "primary.main" : "transparent", color: viewMode === "card" ? "white" : "text.secondary", "&:hover": { bgcolor: viewMode === "card" ? "primary.dark" : "action.hover" } }}
                >
                  <Box component="span" display="flex" alignItems="center" gap={0.5}><ViewModuleRounded fontSize="small" /> </Box>
                </IconButton>
              </Tooltip>
              <Tooltip title="List view">
                <IconButton
                  size="small"
                  onClick={() => setViewMode("list")}
                  sx={{ borderRadius: 0, bgcolor: viewMode === "list" ? "primary.main" : "transparent", color: viewMode === "list" ? "white" : "text.secondary", "&:hover": { bgcolor: viewMode === "list" ? "primary.dark" : "action.hover" } }}
                >
                  <Box component="span" display="flex" alignItems="center" gap={0.5}><ViewListRounded fontSize="small" /> </Box>
                </IconButton>
              </Tooltip>
            </Box>

            <Tooltip title={isExporting ? "Exporting..." : "Data Export to Excel"}>
              <img
                src={excelIcon}
                alt="Export"
                style={{
                  cursor: isExporting ? "not-allowed" : "pointer",
                  width: 33,
                  height: 33,
                  marginBottom: "-7px",
                  opacity: isExporting ? 0.5 : 1,
                  transition: "opacity 0.2s ease",
                }}
                onClick={handleExport}
              />
            </Tooltip>

          </Box>
        </Grid>
      </Grid>
      {viewMode === "list" ? (
        <Table
          sx={{ pb: 0, mb: 0, height: '505px' }}
          data={filteredRecords}
          columns={columns}
          loading={isLoading}
          selection={{ selected, setSelected }}
          sortHandler={handleSort}
          onRowClick={(row: any) => setViewRecord(row)}
          pagination={{
            totalCount: filteredRecords.length,
            pageCount: limit,
            setPageCount: setLimit,
            page: offset,
            setPage: setOffset,
          }}
        />
      ) : (

        <Box pl={2} pr={2} pb={2}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <Typography color="text.secondary">Loading...</Typography>
            </Box>
          ) : filteredRecords.length === 0 ? (
            <Box display="flex" justifyContent="center" py={6}>
              <Typography color="text.secondary">No records found</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Divider sx={{ mb: 2.5 }} />
              {filteredRecords.map((row: any, i: number) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <KybDocumentCard row={row} onClick={() => setViewRecord(row)} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
      <ValidateAccess name={Permissions.EDIT_EXPIRY_MANAGEMENT}>
        <KybExpiryView
          open={!!viewRecord}
          onClose={() => setViewRecord(null)}
          data={viewRecord}
        />
      </ValidateAccess>
      <ValidateAccess name={Permissions.CREATE_EXPIRY_MANAGEMENT}>
        <AddKYBOrg open={open} setOpen={setOpen} />
        <FloatingButton onClick={() => setOpen(true)} />
      </ValidateAccess>
    </Box >
  );
}


const diffDays = (expiryDate: string) =>
  moment(expiryDate).startOf("day").diff(moment().startOf("day"), "days");

const getKybStatus = (expiryDate: string, isExpiry: boolean) => {
  if (!isExpiry || !expiryDate) return { label: "No Expiry", color: "#14B8A6", days: null };
  const days = diffDays(expiryDate);
  if (days < 0) return { label: "Expired", color: "#9CA3AF", days };
  const expiry = moment(expiryDate).startOf("day");
  const startOfWeek = moment().startOf("week"); // Sunday
  const endOfWeek = moment().endOf("week");     // Saturday
  if (expiry.isBetween(startOfWeek, endOfWeek, undefined, "[]"))
    return { label: "Critical", color: "#EF4444", days };
  const startOfMonth = moment().startOf("month");
  const endOfMonth = moment().endOf("month");
  if (expiry.isBetween(startOfMonth, endOfMonth, undefined, "[]"))
    return { label: "Warning", color: "#F59E0B", days };
  return { label: "On Track", color: "#14B8A6", days };
};

const KybDocumentCard = ({ row, onClick }: { row: any; onClick: () => void }) => {
  const entity = row?.client || row?.clientGroup;
  const clientName = entity?.displayName || "—";
  const clientId = entity?.clientId || entity?.clientGroupId || "";
  const status = getKybStatus(row.expiryDate, row.isExpiry);

  return (
    <Card
      onClick={onClick}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: `0.5px solid Black`,
        cursor: "pointer",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 3px 4px rgba(0,0,0,0.20)",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 4px 14px rgba(0,0,0,0.30)" },
      }}
    >
      {/* Top row: icon + days */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box
          sx={{
            display: "flex", flexDirection: "column", alignItems: "left", justifyContent: "center",
          }}
        >
          {/* Document name */}
          <Typography
            fontSize={16}
            // fontWeight={600}
            lineHeight={1.3}
            gutterBottom
          >
            {documentTypes.find((x) => x.value === row.documentName)?.label || row.documentName}
          </Typography>

          {/* Client name */}
          <Box display="flex" alignItems="center" gap={0.5} mb={0.3}>
            <BusinessOutlined
              sx={{
                fontSize: 13,
                color: "text.secondary",
              }}
            />
            <Typography
              fontSize={13}
              fontWeight={400}
              color="text.secondary"
              noWrap
            >
              {clientName}
            </Typography>
          </Box>

          {/* Client ID · Doc number */}
          <Typography
            fontSize={12}
            fontWeight={400}
            color="text.disabled"
            display="block"
            mb={1.5}
          >
            {[clientId, row.documentNumber].filter(Boolean).join(" · ")}
          </Typography>

        </Box>

        <Box textAlign="right">
          {row.isExpiry && row.expiryDate ? (
            <>
              <Typography
                fontSize={status.days === 0 ? 14 : 30}
                fontWeight={700}
                color={status.color}
                lineHeight={1}
              >
                {status.days !== null && status.days < 0 ? "—" : status.days === 0 ? "Expiring Today" : status.days}
              </Typography>

              {status.days !== null && status.days !== 0 && (
                <Typography fontSize={12} fontWeight={400} color="text.secondary">
                  {status.days < 0 ? "expired" : "days left"}
                </Typography>
              )}
            </>
          ) : (
            <Typography
              fontSize={12}
              fontWeight={400}
              color="text.disabled"
            >
              No Expiry
            </Typography>
          )}
        </Box>
      </Box>



      {/* Progress bar */}
      {/* <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          mb: 2, height: 4, borderRadius: 999, bgcolor: "#E5E7EB",
          "& .MuiLinearProgress-bar": { bgcolor: status.color },
        }}
      /> */}

      {/* Footer */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt="auto">
        <Box display="flex" alignItems="center" gap={0.5}>
          <CalendarTodayOutlined sx={{ fontSize: 13, color: "text.secondary" }} />
          <Typography
            fontSize={12}
            fontWeight={400}
            color="text.secondary"
          >
            {row.isExpiry && row.expiryDate
              ? `${status.days !== null && status.days < 0 ? "Expired" : "Expires"} ${moment(row.expiryDate).format("DD MMM YYYY")}`
              : "No Expiry"}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Chip
            label={status.label}
            size="small"
            sx={{ bgcolor: `${status.color}18`, color: status.color, fontWeight: 600, fontSize: 11 }}
          />
          {row?.storage?.fileUrl && (
            <Tooltip title="Download attachment">
              <IconButton
                size="small"
                component="a"
                href={row.storage.fileUrl}
                download
                target="_blank"
                rel="noreferrer"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
              >
                <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Card>
  );
};

const ExpiryDateCell = ({ row }: { row: any }) => {
  if (!row?.expiryDate) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const status = getKybStatus(row.expiryDate, row.isExpiry);
  return (
    <Box minWidth={110}>
      <Typography variant="body2" color={status.color} fontWeight={status.label === "Critical" ? 700 : 400}>
        {moment(row.expiryDate).format("DD MMM YYYY")}
      </Typography>
    </Box>
  );
};

const StatusBadge = ({ row }: { row: any }) => {
  const status = getKybStatus(row.expiryDate, row.isExpiry);
  const days = status.days;
  const label = days === null
    ? status.label
    : days < 0
    ? "Expired"
    : days === 0
    ? "Today"
    : `${days} days`;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, bgcolor: `${status.color}18`, color: status.color, borderRadius: 5, px: 1.2, py: 0.4 }}>
      <Typography variant="caption" fontWeight={600} color="inherit">{label}</Typography>
    </Box>
  );
};

const defaultColumns: any = [
  {
    key: "client",
    title: "Client",
    render: (row: any) => {
      const entity = row?.client || row?.clientGroup;
      const name = entity?.displayName || "—";
      const id = entity?.clientId || entity?.clientGroupId || "";
      const img = entity?.image?.fileUrl;
      if (!entity) return null;
      return (
        <Box display="flex" alignItems="center" gap={1}>
          {img ? (
            <Box component="img" src={img} sx={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }} />
          ) : (
            <Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: "#1976D2", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
              {name.charAt(0).toUpperCase()}
            </Box>
          )}
          <Box>
            <Typography variant="body2" fontWeight={600} lineHeight={1.2}>{name}</Typography>
            <Typography variant="caption" color="text.secondary">{id}</Typography>
          </Box>
        </Box>
      );
    },
  },
  {
    key: "documentName",
    title: "Document Type",
    // sort: true,
    // active: false,
    direction: SortDirection.ASC,
    default: true,
  },
  {
    key: "documentNumber",
    title: "Document ID",
    // sort: true,
    // active: false,
    direction: SortDirection.ASC,
    default: true,
  },
  {
    key: "expiryDate",
    title: "Expiry Date",
    // sort: true,
    // active: false,
    direction: SortDirection.ASC,
    default: true,
    render: (row: any) => <ExpiryDateCell row={row} />,
  },
  {
    key: "expiryStatus",
    title: "Status",
    // sort: true,
    // active: false,
    direction: SortDirection.ASC,
    default: true,
    render: (row: any) => <StatusBadge row={row} />,
  },
];

export default ExpiryMgmt;